import { AddonTypeEnum } from '../../models/cartDetails/addonType.enum';
import { Inject, Injectable } from '@angular/core';
import { PrimeAddonsService } from './prime-addons.service';
import ManageTvService from '../manage-tv/manage-tv.service';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import { animate, AnimationBuilder, style } from '@angular/animations';
import { IAddon, IGroupedAddon, IObligatoryAddon, IPriceSet } from '../../interfaces/cart-details.interface';
import { PriceTypeEnum } from '../../models/cartDetails/priceType.model';

declare const window: any;
declare const document: any;

@Injectable()
export class CommonAddonsService {

  private slotAddonsToAdd: any = {
    addonsToAdd: [],
    bundleNo: null
  };

  public ADDON_ANIMATION_DURATION = 300;

  constructor(
    private manageTvService: ManageTvService,
    private primeAddonsService: PrimeAddonsService,
    private notificationProvider: NotificationProviderService,
    private animationBuilder: AnimationBuilder,
    @Inject('Endpoint') private endpoint: any,
  ) {
  }

  // 1
  public prepareSlotAddons(slot: any) {
    const computedFields = slot._computedFields;

    // addony opcjonalne
    this.makeAddonsComputedFields(slot);

    // mapa addonow opcjonalnych
    this.primeAddonsService.prepareAddonsMap(slot);

    const addonsArray = this.getAllAddonsAsArray(slot.addons);

    computedFields.visibleOptionalAddons = this.filterVisibleAddons(slot.addons.optional);
    computedFields.selectedAddons = this.filterSelectedAddons(addonsArray);
    computedFields.selectedOptionalAddons = this.filterSelectedOptionalAddons(addonsArray);

    computedFields.selectedAddons.forEach((addon: IAddon) => {
      addon._computedFields = addon._computedFields || {};
      addon._computedFields.isDataOffer = slot.isDataOffer;
    });

    computedFields.selectedVisibleOptionalAddonsCodes = computedFields.visibleOptionalAddons
      .filter((addon: any) => addon.selected)
      .map((addon: any) => addon.addonCode);


    this.prepareGroupedAddon(slot);

    //addons short name, character limit to show - 35
    let totalNumberOfCharacters = 0;
    const shortAddonsList = computedFields.visibleOptionalAddons.reduce((accu: any, addon: any) => {
      if (addon.displayName) {
        const addonLength = addon.displayName.length;

        if (!addon.selected && totalNumberOfCharacters + addonLength < 35) {
          totalNumberOfCharacters += addonLength;

          accu.push(addon.displayName);

          if (totalNumberOfCharacters < 35) {
            totalNumberOfCharacters += 1;
          }
        }
      }

      return accu;
    }, []);

    computedFields.addOnsShortNameList = shortAddonsList.join(', ');

    computedFields.showAddOnsLink = computedFields.visibleOptionalAddons.length
      && computedFields.visibleOptionalAddons.length - computedFields.selectedOptionalAddons.length
      || computedFields.groupedAddon
      && !computedFields.groupedAddon.selected;

    computedFields.addOnsLinkShowOthers = !!shortAddonsList.length &&
      shortAddonsList.length < computedFields.visibleOptionalAddons.length - computedFields.selectedOptionalAddons.length;

  }

  public prepareSecondPageSlotAddons(slot: any) {
    this.makeAddonsComputedFields(slot);

    this.prepareGroupedAddon(slot);

    this.appendManageTVmsisdn(slot.addons, slot.isDataOffer);

    slot._computedFields.selectedAddons = this.filterSelectedAddons(this.getAllAddonsAsArray(slot.addons));
  }

  public slideAddon(ordinalNumber: any, addonCode: any, direction: any) {
    const self = this;

    const element = document.querySelector('[data-tile-id="new-number-' + ordinalNumber + '"] [data-addon-code="' + addonCode + '"]');

    let animationStyle;

    if (direction === 'slideDown') {
      animationStyle = [
        style({ height: 0, overflow: 'hidden' }),
        animate(`${this.ADDON_ANIMATION_DURATION}ms ease-in-out`, style({ height: '*' }))
      ];
    } else {
      animationStyle = [
        style({ height: '*', overflow: 'hidden' }),
        animate(`${this.ADDON_ANIMATION_DURATION}ms ease-in-out`, style({ height: 0 }))
      ];
    }

    const toggle = this.animationBuilder.build(animationStyle);
    const player = toggle.create(element);
    player.play();

    if (direction === 'slideDown') {
      setTimeout(() => {
        self.removeFromSlotAddonsToAdd(addonCode);
      }, this.ADDON_ANIMATION_DURATION + 50);
    }
  }

  public setSlotAddonsToAdd(addonsToAdd: string[], bundleNo: number) {
    this.slotAddonsToAdd = {
      addonsToAdd,
      bundleNo
    };
  }

  public removeAddon(addonCode: any, bundleNumber: number, slotOrdinalNumber: number) {
    this.endpoint('addons.delete', {
      addonCodes: [addonCode],
      bundleNo: bundleNumber
    }).queue('addons')
      .run()
      .then((response: any) => {
        this.slideAddon(slotOrdinalNumber, addonCode, 'slideUp');

        setTimeout(() => {
          this.notificationProvider.publish(
            NotificationProviderTypeEnum.CART_DETAILS,
            response
          );
        }, this.ADDON_ANIMATION_DURATION);
      });
  }

  private addonsOrder(addon: any) {
    return addon.addonType === AddonTypeEnum.PRIME ? 1 : 0;
  }

  private getAllAddonsAsArray(_addons: any) {
    _addons.optional = _addons.optional.sort((addon1: any, addon2: any) => {
      return this.addonsOrder(addon2) - this.addonsOrder(addon1);
    });

    const addonGroups = ['transitive', 'obligatory', 'optional', 'grouped'];

    return addonGroups.reduce((result: any, addonGroup: any) => {
      let addons = _addons[addonGroup];

      // show subaddons only when group is selected
      if (addonGroup === 'grouped' && addons.length && addons[0].selected) {
        addons = addons[0].groupedAddons;
      }

      addons.forEach((addon: any) => addon.addonGroup = addonGroup);

      // move tv addon on the top (ITAEC-5012)
      if (addonGroup == 'obligatory') {
        const tvAddon = addons.find((addon: any) => addon.addonType === AddonTypeEnum.TV);

        if (tvAddon) {
          addons = addons.filter((addon: any) => addon.addonType !== AddonTypeEnum.TV);
          result.unshift(tvAddon);
        }
      }

      result.push(...addons);

      return result;
    }, []);
  }

  private filterVisibleAddons(addonsArray: any) {
    return addonsArray.filter((addon: any) => !addon.hideComponent);
  }

  private filterSelectedAddons(addonsArray: any) {
    return addonsArray.filter((addon: any) => addon.selected);
  }

  private filterSelectedOptionalAddons(addonsArray: any) {
    return addonsArray.filter((addon: any) => addon.selected && addon.addonGroup === 'optional');
  }

  private makeAddonsComputedFields(slot: any) {
    slot.addons.optional.forEach((addon: IAddon) => {
      addon._computedFields = {
        monthlyPrices: addon.priceInfos.find((priceGroup: IPriceSet) => priceGroup.type === PriceTypeEnum.MONTHLY)?.prices,
        termOfServiceNumber: slot.termOfServiceNumber,
        showStatement: false,
        dependencyMessage: (addon.addonType && addon.addonType === AddonTypeEnum.PRIME)
          ? window.PRIME_MAIN_ADDON_TEXT
          : window.PRIME_DEPENDENT_ADDON_TEXT,
        readOnly: false
      };
    });

    slot.addons.transitive.forEach((addon: any) => {
      addon._computedFields = {
        monthlyPrices: addon.priceInfos.find((priceGroup: IPriceSet) => priceGroup.type === PriceTypeEnum.MONTHLY)?.prices,
        termOfServiceNumber: slot.termOfServiceNumber
      };
    });
  }

  private prepareGroupedAddon(slot: any) {
    // zakładamy, że tablica slot.addons.grouped zawiera co najwyżej jeden element
    const isVisibleAddonInGroup = slot.addons.grouped
      && slot.addons.grouped[0]
      && slot.addons.grouped[0].groupedAddons
      && slot.addons.grouped[0].groupedAddons.find((addon: IGroupedAddon) => !addon.hideComponent);

    if (isVisibleAddonInGroup) {
      const groupedAddon = slot.addons.grouped[0];
      const selectedSubAddon = groupedAddon.groupedAddons.find((addon: IGroupedAddon) => addon.selected);

      groupedAddon._computedFields = {
        selectedSubAddonCode: selectedSubAddon ? selectedSubAddon.addonCode : null
      };

      groupedAddon.groupedAddons = groupedAddon.groupedAddons
        .filter((addon: any) => !addon.hideComponent);

      groupedAddon.groupedAddons.forEach((addon: IAddon) => {
          addon._computedFields = {
            monthlyPrices: addon.priceInfos.find((priceGroup: IPriceSet) => priceGroup.type === PriceTypeEnum.MONTHLY)?.prices,
            termOfServiceNumber: slot.termOfServiceNumber,
            dependencyMessage: window.PRIME_MAIN_ADDON_TEXT,
            showStatement: false
          };
        }
      );

      slot._computedFields.groupedAddon = groupedAddon;
    }
  }

  private appendManageTVmsisdn(addonsArray: any, isDataOffer: any) {
    if (!isDataOffer) {
      return;
    }

    addonsArray.obligatory.forEach((addon: IObligatoryAddon) => {
      if (addon.addonType === AddonTypeEnum.TV) {
        addon.msisdnManageTv = this.manageTvService.msisdn;
      }
    });
  }

  private removeFromSlotAddonsToAdd(addonCode: any) {
    const index = this.slotAddonsToAdd.addonsToAdd.indexOf(addonCode);

    if (index !== -1) {
      this.slotAddonsToAdd.addonsToAdd.splice(index, 1);
    }
  }

}
